import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  LinkedIn as LinkedInIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: '700px',
    width: '100%',
    margin: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(0.5),
      borderRadius: theme.spacing(1),
      maxHeight: '95vh',
    },
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#0077B5', // LinkedIn blue
  color: 'white',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& .MuiIconButton-root': {
    color: 'white',
  },
}));

const OrganizationListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
}));

const LinkedInOrgSelectionModal = ({ open, organizations = [], onClose, onSubmit }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Filter organizations based on search
  const filteredOrganizations = organizations.filter(org => 
    org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.vanity_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    // Reset selections when modal opens
    if (open) {
      setSelectedOrgs([]);
      setSelectAll(false);
      setSearchTerm('');
    }
  }, [open]);
  
  const handleToggleOrganization = (orgId) => {
    setSelectedOrgs(prev => {
      if (prev.includes(orgId)) {
        return prev.filter(id => id !== orgId);
      } else {
        return [...prev, orgId];
      }
    });
  };
  
  const handleSelectAll = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedOrgs(filteredOrganizations.map(org => org.id));
    } else {
      setSelectedOrgs([]);
    }
  };
  
  const handleSubmit = async () => {
    if (selectedOrgs.length === 0) {
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(selectedOrgs);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const formatFollowerCount = (count) => {
    if (!count) return '0';
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}K`;
    }
    return count.toString();
  };
  
  return (
    <StyledDialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
    >
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LinkedInIcon />
          <Typography variant="h6" component="span">
            Select LinkedIn Organizations
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          {organizations.length === 0 ? (
            <Alert severity="warning">
              No LinkedIn organizations found. This could be due to:
              <ul style={{ marginTop: '8px', marginBottom: '0' }}>
                <li>You may not have administrator access to any organizations</li>
                <li>LinkedIn rate limiting - please try again in a few minutes</li>
                <li>Network connectivity issues</li>
              </ul>
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Select the organizations you want to connect. You can manage multiple organization accounts.
              </Alert>
              
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectAll && filteredOrganizations.length > 0}
                      onChange={handleSelectAll}
                      disabled={filteredOrganizations.length === 0}
                    />
                  }
                  label="Select All"
                />
                <Typography variant="body2" color="text.secondary">
                  {selectedOrgs.length} of {filteredOrganizations.length} selected
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {filteredOrganizations.map((org) => (
                  <OrganizationListItem
                    key={org.id}
                    button
                    onClick={() => handleToggleOrganization(org.id)}
                    selected={selectedOrgs.includes(org.id)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedOrgs.includes(org.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    
                    <ListItemIcon>
                      {org.logo_url ? (
                        <Avatar
                          src={org.logo_url}
                          alt={org.name}
                          sx={{ width: 48, height: 48 }}
                        >
                          <BusinessIcon />
                        </Avatar>
                      ) : (
                        <Avatar sx={{ width: 48, height: 48, bgcolor: '#0077B5' }}>
                          <BusinessIcon />
                        </Avatar>
                      )}
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="medium">
                          {org.name || `Organization ${org.id}`}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {org.vanity_name && (
                            <Typography variant="caption" color="text.secondary">
                              @{org.vanity_name}
                            </Typography>
                          )}
                          <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip
                              icon={<PeopleIcon />}
                              label={`${formatFollowerCount(org.followers_count)} followers`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      }
                    />
                  </OrganizationListItem>
                ))}
              </List>
              
              {filteredOrganizations.length === 0 && searchTerm && (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No organizations found matching "{searchTerm}"
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={selectedOrgs.length === 0 || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          sx={{
            backgroundColor: '#0077B5',
            '&:hover': {
              backgroundColor: '#005885',
            },
          }}
        >
          {isSubmitting 
            ? 'Connecting...' 
            : `Connect ${selectedOrgs.length} Organization${selectedOrgs.length !== 1 ? 's' : ''}`
          }
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default LinkedInOrgSelectionModal;